import { AuthContext } from "AuthContext"
import { useContext, useEffect, useRef, useState } from "react"
import Loader from "react-spinners/BeatLoader"
import { useModel } from "@stem-sims/nexus"
import assignmentsModel from "shared/routes/assignments"
import styles from './assessment.module.scss'
import assessments from "shared/routes/assessments/assessments"
import moduleRoutes from "shared/routes/moduleRoutes"
import { DrawingAnswer } from "shared/types/studentTypes"
import AssessmentQuestion from "./AssessmentQuestion"
import Button from "react-bootstrap/Button"
import { Formik } from "formik"
import ImageLibrary from "components/Editing/ImageLibrary"
import { Form, Modal } from "react-bootstrap"

interface AssessmentProps {
  submissionId?: string
  assessmentId: string
  moduleName: string
  assessmentNumber: number
  type: string
  initialAssessmentData?: Record<string, string>
  initialAssessmentDrawingData?: Record<string, DrawingAnswer>
  updateDrawingResponses?: (props: { questionNumber: number, answer: string, base64String: string, updateInitialState?: boolean }) => void
  isPartOfTopic: boolean
  debouncedSaveAssessment?: () => Promise<void>
  oneQuestionMode?: boolean
  randomizable: boolean
  isReview: boolean
  submitEnabled?: (enabled: boolean) => void
}


const Assessment = (props: AssessmentProps) => {
  const { assessmentId, initialAssessmentData, initialAssessmentDrawingData, updateDrawingResponses, oneQuestionMode = false, submitEnabled } = props
  const authInformation = useContext(AuthContext)
  const editing = process.env.REACT_APP_EDITING === "true"

  const [singleQuestionMode, setSingleQuestionMode] = useState(oneQuestionMode ?? false) // state for updating the mode within this component
  const formRef = useRef(null)

  const { response: assessmentQuestions, loading, refreshModel } = useModel({
    model: assignmentsModel.getAssessmentQuestions,
    props: { assessmentId, randomizable: props.randomizable ?? false }
  })

  const { response: assessmentQuestionContexts, loading: loadingAssessmentQuestionContexts, refreshModel: refreshAssessmentQuestionContexts } = useModel({
    model: assignmentsModel.getAssessmentQuestionContexts,
    props: { assessmentId }
  })

  const { response: assessment, loading: loadingAssessment } = useModel({
    model: assessments.findById,
    props: { id: assessmentId }
  })

  useEffect(() => {
    formRef.current?.reset()
  }, [initialAssessmentData])

  const formattedModuleName = assessment?.lessonId ?
  `Lesson ${assessment.lessonNumber} Assessment: ${props.moduleName}` :
  `Assessment ${assessment?.number}: ${props.moduleName}`

  useEffect(() => {
    const updateModuleHit = async (moduleName: string) => {
        await moduleRoutes.updateModuleHit(moduleName)
    }

    if (authInformation.isLoggedIn && authInformation.isStudent) {
        updateModuleHit(props.moduleName)
    }
  }, [authInformation.isLoggedIn, authInformation.isStudent, props?.moduleName])

  // One Question Mode Navigation
  const [currentQuestion, setCurrentQuestion] = useState(0)

  useEffect(() => {
    if (submitEnabled) {
      submitEnabled(!singleQuestionMode || assessmentQuestions?.length <= 1 || currentQuestion === assessmentQuestions?.length - 1)
    }
  }, [assessmentQuestions, currentQuestion, singleQuestionMode, submitEnabled])

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1)
    }
  }

  const nextQuestion = () => {
    if (assessmentQuestions !== null && currentQuestion < assessmentQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1)
    }
  }

  const [ilModal, setILModal] = useState<string>(null)

  const [modalVisible, setModalVisible] = useState(false)
  const [editedContextId, setEditedContextId] = useState<string | null>(null)
  const [editedContextText, setEditedContextText] = useState<string>("")

  const handleAddContext = async (values: { contextText: string }) => {
    if (values.contextText.trim()) {
      assignmentsModel.updateAssessmentQuestionContext({
        contextId: null,
        text: values.contextText,
        assessmentId: props.assessmentId
      }).then(() => {
        refreshAssessmentQuestionContexts()
        values.contextText = ""
      })
    }
  }

  const handleUpdateContext = async () => {
    if (editedContextId && editedContextText.trim()) {
      await assignmentsModel.updateAssessmentQuestionContext({
        contextId: editedContextId,
        text: editedContextText,
        assessmentId: props.assessmentId
      }).then(() => {
        refreshAssessmentQuestionContexts()
      })
      setEditedContextId(null)
      setEditedContextText("")
    }
  }

  const handleDeleteContext = async (contextId: string) => {
    await assignmentsModel.deleteAssessmentQuestionContext({
      contextId,
    }).then(() => {
      refreshAssessmentQuestionContexts()
    })
  }

  let assessmentHeader = ""
  if (!props.isReview) {
    assessmentHeader = formattedModuleName
  } else if (assessment?.lessonId) {
    assessmentHeader = "Recheck"
  } else {
    assessmentHeader = "Knowledge Check"
  }

  return (
    <form className={styles.assessment} id="assessment-form" ref={formRef}>
      { editing && <a href={`/editing/simulations/assessment?&module=${props.moduleName}&assessmentNumber=${props.assessmentNumber}&type=${props.type}`}>Old Assessment Page For Editing</a> }
        {loadingAssessment ?
        <Loader color={"#007bff"} /> :
        <h1 className={"h4 fw-bold"}>
          {assessmentHeader}
        </h1>}
        { editing && <div className="my-4 d-flex justify-content-center align-items-center">
            <div className="me-2">
              <Form>
                <Form.Check
                  type="switch"
                  id="oneQuestionModeSwitch"
                  className="fw-bold"
                  label="One Question Mode"
                  onChange={(e) => {
                    setSingleQuestionMode(e.target.checked)
                  }}
                />
              </Form>
            </div>
            <Button variant="primary" className="me-2" onClick={() => setModalVisible(true)}>Manage Contexts</Button>
            <Button variant="primary" onClick={() => setILModal("imageLibrary")}>Image Library</Button>
        </div> }
        <div className="mt-4">
            {loading && !assessmentQuestions && <Loader color={"#007bff"} />}
        </div>
        {Boolean(singleQuestionMode) && assessmentQuestions !== null && (
          <div className="d-flex justify-content-end align-items-center container mb-4 bg-white w-100" style={{ position: "sticky", top: 0, zIndex: 2 }}>
            <Button variant="primary" onClick={() => previousQuestion()} disabled={currentQuestion === 0} className="me-3">Previous Question</Button>
            <span className="me-3 fw-bold text-muted">{currentQuestion + 1} / {assessmentQuestions.length}</span>
            <Button variant="primary" onClick={() => nextQuestion()} disabled={currentQuestion === assessmentQuestions.length - 1} >Next Question</Button>
          </div>)}
        {renderQuestions()}

        {contextModal()}
        {editing && <ImageLibrary modal={ilModal} setModal={setILModal} />}
    </form>
  )

  function renderQuestions() {
    if (loadingAssessmentQuestionContexts) {
      return <Loader color={"#007bff"} />
    }
    // In one question mode, show the contexts every question. Otherwise, only show the context for the first question in a run with the same context
    let lastContextId = null    
    return assessmentQuestions?.map((question, index) => {
      const showContext = (singleQuestionMode || question.contextId !== lastContextId) && question.contextId !== null
      const context = assessmentQuestionContexts?.find(context => context.contextId === question.contextId)?.text
      lastContextId = question.contextId
      return <AssessmentQuestion 
        submissionId={props.submissionId}
        key={`${question.question} - ${index}`}
        assessmentId={assessmentId}
        assessmentNumber={props.assessmentNumber}
        index={index}
        initialAnswerData={question.isDrawing === "Yes" ? initialAssessmentDrawingData?.[`question-${question.number}`] : initialAssessmentData?.[`question-${question.number}`]}
        moduleName={props.moduleName}
        totalQuestionsCount={assessmentQuestions?.length}
        question={question}
        saveAssessment={props.debouncedSaveAssessment}
        updateDrawingResponses={updateDrawingResponses}
        type={props.type}
        refreshQuestions={() => {
          refreshModel()
          refreshAssessmentQuestionContexts()
        }}
        visible={!singleQuestionMode || currentQuestion === index}
        showContext={showContext}
        context={context}
        />
    })
  }

  function contextModal() {
    return (
        <Modal show={modalVisible} onHide={() => setModalVisible(false)} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Manage Question Contexts</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <Formik 
                initialValues={{ contextText: "" }}
                onSubmit={handleAddContext}
              >
                {({ values, handleChange, handleSubmit }) => (
                  <form onSubmit={handleSubmit}>
                    <input 
                      type="textarea" 
                      name="contextText"
                      id="contextText"
                      value={values.contextText} 
                      onChange={handleChange} 
                      placeholder="Add new context" 
                      className="form-control" 
                    />
                    <button 
                      type="submit" 
                      disabled={!values.contextText.trim()} 
                      className="btn btn-primary mt-2"
                    >
                      Add Context
                    </button>
                  </form>
                )}
              </Formik>
            </div>

            {assessmentQuestionContexts?.map(context => (
              <div key={context.contextId} className="mb-3">
                {editedContextId === context.contextId ? (
                  <div className="d-flex align-items-center">
                    <textarea 
                      rows={10}
                      value={editedContextText} 
                      onChange={(e) => setEditedContextText(e.target.value)} 
                      className="form-control me-2"
                    />
                    <Button 
                      variant="primary"
                      onClick={handleUpdateContext}
                    >
                      Save
                    </Button>
                    <Button variant="secondary" className="ms-2" onClick={() => setEditedContextId(null)}>Cancel</Button>
                  </div>
                ) : (
                  <div className="d-flex justify-content-between align-items-center">
                    <p className="mb-0">{context.text}</p>
                    <div>
                      <Button 
                        onClick={() => {
                          setEditedContextId(context.contextId)
                          setEditedContextText(context.text)
                        }} 
                        variant="secondary"
                        className="me-2"
                      >
                        Edit
                      </Button>
                      <Button 
                        variant="danger"
                        onClick={() => handleDeleteContext(context.contextId)} 
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {assessmentQuestionContexts?.length === 0 && (
              <p className="text-muted">No contexts available. Add one above!</p>
            )}
          </Modal.Body>
        </Modal>
    )
  }

}

export default Assessment
